import classNames from 'classnames';
import { InfoIcon } from 'components/Icons';
import Tooltip from 'components/Tooltip';
import type { DateTime, Duration } from 'luxon';
import React, { Component } from 'react';
import FontAwesome from 'react-fontawesome';
import type { HourTimestamp } from 'types/hour';
import type { UserTimeFormat } from 'types/user';
import { formatTime } from 'utils/date-helper';
import { fromISO } from 'utils/luxon-helpers';
import { guessTimestampFromText, parseTime } from 'utils/time-helper';
import { Section } from './Section';
import styles from './TimestampPickerSection.module.scss';

interface Props {
  timestamp: HourTimestamp;
  date: string;
  onClear?: () => void;
  onChange: (value: HourTimestamp) => void;
  disabled?: boolean;
  showInput: boolean;
  isTimerRunning: boolean;
  userTimeZone?: string;
  timeFormat: UserTimeFormat;
  snapToMinutes: number;
  standalone?: boolean;
  compactStyle?: boolean;
}

interface State {
  from: string | null | undefined;
  to: string | null | undefined;
}

export class TimestampPickerSection extends Component<Props, State> {
  state: State = {
    from: null,
    to: null,
  };

  fromInput = React.createRef<HTMLInputElement>();
  toInput = React.createRef<HTMLInputElement>();

  triggerUpdate = (
    event: React.SyntheticEvent<HTMLInputElement>,
    viaKey: string | null | undefined = null
  ) => {
    const { name: fieldName } = event.currentTarget;
    const value = guessTimestampFromText(event.currentTarget.value);
    const parsed = parseTime(value);
    // UP/down keys should adjust the value:
    const secondsAdjustment =
      viaKey != null && ['ArrowUp', 'ArrowDown'].includes(viaKey)
        ? this.props.snapToMinutes * 60
        : 0;
    const secondsOffset =
      viaKey === 'ArrowDown' ? Math.abs(secondsAdjustment) * -1 : secondsAdjustment;

    if (parsed) {
      const { hour: hours, minute: minutes } = parsed;

      const newFieldValue = fromISO(this.props.date)
        .startOf('day')
        .startOf('minute')
        .plus({ hours, minutes, seconds: secondsOffset })
        .toISO();

      const change: HourTimestamp = this.validateChange(fieldName, {
        ...this.props.timestamp,
        [fieldName]: newFieldValue,
      });

      this.props.onChange(change);
    }

    const hasValidKnownValues = parsed != null;

    // Reset all stateful input
    this.setState({ from: null, to: null });

    // Be smart about next focus when user hits enter on 'from':
    if (viaKey === 'Enter' && fieldName === 'from') {
      if (hasValidKnownValues) {
        this.handleInputFocus(event, 'to');
      } else {
        this.handleInputFocus(event, 'from');
      }
    }

    // When pressing up/down, refocus on value:
    if (secondsAdjustment !== 0) {
      this.handleInputFocus(event);
    }
  };

  handleInputClick = (event: React.SyntheticEvent<HTMLInputElement>) => {
    if (this.props.compactStyle) {
      event.stopPropagation();
    }
  };

  handleInputFocus = (
    event: React.SyntheticEvent<HTMLInputElement>,
    targetName: string | null | undefined = null
  ) => {
    event.persist();
    // NB! Switched these around. If weird shit crops up,
    // make wrapper handler for input.onFocus events
    const target = event.currentTarget.name || targetName;
    setTimeout(() => {
      const ref = target === 'from' ? this.fromInput : this.toInput;
      if (ref.current) {
        ref.current.select();
      }
    }, 20);
  };

  handleInputBlur = (event: React.SyntheticEvent<HTMLInputElement>) => {
    // When clicking back and forth between timestamps, sometimes timestamp is mutated?
    // Hopefully this workaround is a real fix
    if (this.state.from || this.state.to) {
      this.triggerUpdate(event);
    }
  };

  handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'Enter': {
        // Before date is parsed & removed from local state, don't allow ENTER to submit form
        if (this.state[event.currentTarget.name as 'from' | 'to'] !== null) {
          event.preventDefault();
        }
        this.triggerUpdate(event, event.key);
        break;
      }
      case ' ':
      case '-': {
        event.preventDefault();
        this.toInput.current?.select();
        break;
      }
      case 'ArrowUp':
      case 'ArrowDown': {
        this.triggerUpdate(event, event.key);
        break;
      }
      default:
        break;
    }
  };

  handleChangeFrom = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    this.setState({ from: value });
  };

  handleChangeTo = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    this.setState({ to: value });
  };
  render() {
    const { timestamp, timeFormat, disabled, showInput, isTimerRunning, standalone, compactStyle } =
      this.props;
    const shouldShowInput = showInput && !isTimerRunning;

    const from = timestamp.from ? formatTime(timestamp.from, timeFormat) : '';
    const to = timestamp.to ? formatTime(timestamp.to, timeFormat) : '';

    const tooltip = !compactStyle && this.getTimestampTooltip(timestamp);

    return (
      <Section
        standalone={standalone}
        className={classNames(styles.container, {
          [styles.ampm]: this.props.timeFormat === 12,
          [styles.compact]: this.props.compactStyle,
        })}
      >
        <label className={styles.label}>From</label>
        <div className={styles.time}>
          {shouldShowInput ? (
            <input
              className="stayOpen"
              disabled={disabled}
              type="text"
              name="from"
              data-testid="from"
              ref={this.fromInput}
              value={this.state.from !== null ? this.state.from : from}
              onClick={this.handleInputClick}
              onFocus={this.handleInputFocus}
              onBlur={this.handleInputBlur}
              onChange={this.handleChangeFrom}
              onKeyDown={this.handleInputKeyDown}
              autoComplete="off"
            />
          ) : (
            <input disabled value={from} />
          )}
          <div className={styles.underline} />
        </div>
        <label className={styles.label}>to</label>

        {compactStyle && <FontAwesome name="arrow-right" className={styles.arrow} />}

        <div className={styles.time}>
          {showInput ? (
            <input
              className="stayOpen"
              disabled={disabled}
              type="text"
              name="to"
              data-testid="to"
              ref={this.toInput}
              value={this.state.to !== null ? this.state.to : to}
              onClick={this.handleInputClick}
              onFocus={this.handleInputFocus}
              onBlur={this.handleInputBlur}
              onChange={this.handleChangeTo}
              onKeyDown={this.handleInputKeyDown}
              autoComplete="off"
            />
          ) : (
            <input disabled value={to} />
          )}
          <div className={styles.underline} />
        </div>

        {tooltip && (
          <Tooltip className={styles.timestampInfo} position="right" title={tooltip}>
            <InfoIcon />
          </Tooltip>
        )}

        <Tooltip
          className={classNames(styles.clear, { [styles.disabled]: disabled })}
          position="top-left"
          title="Clear from & to"
          onClick={this.props.onClear}
          disabled={disabled}
        >
          <FontAwesome name="times" />
        </Tooltip>
      </Section>
    );
  }

  private validateChange(changedField: string, timestamp: HourTimestamp): HourTimestamp {
    if (!timestamp.from || !timestamp.to) return timestamp;
    const from = fromISO(timestamp.from);
    const to = fromISO(timestamp.to);

    if (changedField === 'from') {
      return this.preserveTimestampDuration(timestamp, from, to);
    } else if (changedField === 'to' && from > to) {
      return this.endTimestampAfterMidnight(timestamp, from, to);
    } else {
      return timestamp;
    }
  }

  private preserveTimestampDuration(
    timestamp: HourTimestamp,
    from: DateTime<true>,
    to: DateTime<true>
  ): HourTimestamp {
    const originalFrom = fromISO(this.props.timestamp.from);
    const changedAmount = from.diff(originalFrom);
    const preservedDurationTo = to.plus(changedAmount).toISO();

    return { ...timestamp, from: from.toISO(), to: preservedDurationTo };
  }

  private endTimestampAfterMidnight(
    timestamp: HourTimestamp,
    from: DateTime<true>,
    to: DateTime<true>
  ): HourTimestamp {
    const toNextDay = to.plus({ day: 1 }).toISO();
    return { ...timestamp, from: from.toISO(), to: toNextDay };
  }

  private getTimestampTooltip(timestamp: HourTimestamp): string | null {
    if (!timestamp.from || !timestamp.to) return null;
    const from = fromISO(timestamp.from);
    const to = fromISO(timestamp.to);
    const isNotFullMinute = from.second !== 0 || to.second !== 0;

    if (isNotFullMinute) {
      return this.formatTimestampDuration(to.diff(from));
    } else {
      return null;
    }
  }

  private formatTimestampDuration(duration: Duration): string | null {
    const { hours, minutes, seconds } = duration.shiftTo('hours', 'minutes', 'seconds');
    let format = '';

    if (seconds === 0) {
      return null;
    }

    if (hours > 0) {
      format += `${hours}h `;
    }

    if (minutes > 0) {
      format += `${minutes}m `;
    }

    format += `${seconds}s`;

    return format;
  }
}

export default TimestampPickerSection;
